/// <amd-module name="Wiki/PageComments/Components/WikiPageComments" />
import * as React from "react";

import { equals } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";

import { CommentThread } from "Comments/Thread/CommentThread";
import { WikiArtifact } from "Wiki/PageComments/Contracts";
import * as Resources from "Wiki/PageComments/Resources";
import { WikiPageCommentsClient } from "Wiki/PageComments/WikiPageCommentsClient";
import { buildCommentArtifactInfo } from "Wiki/PageComments/WikiPageCommentsHelper";
import { UrlConstants } from "Wiki/ViewCommon/Constants";

export interface IWikiPageCommentsProps {
    wikiArtifact: WikiArtifact;
    isReadOnly: boolean;
}

export interface IWikiPageCommentState {
    isDirty?: boolean;
}

export class WikiPageComments extends VssComponent<IWikiPageCommentsProps, IWikiPageCommentState> {
    private _wikiPageCommentsClient: WikiPageCommentsClient;
    private _supportsImageUpload: boolean;
    private _supportsReactions = false;
    constructor(props: IWikiPageCommentsProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this._wikiPageCommentsClient = new WikiPageCommentsClient(context.pageContext);

        this._supportsImageUpload = isFeatureFlagEnabled(context.pageContext, "Wiki.WikiPageComments.ImageUpload");
        this._supportsReactions = isFeatureFlagEnabled(context.pageContext, "Wiki.WikiPageCommentReactions");
    }

    public componentDidMount(): void {
        super.componentDidMount();

        document.body.addEventListener("fpsStarting", this._onFpsStarting);
        window.addEventListener("beforeunload", this._onBeforeUnload);

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentState = historyService.getState();
        const shouldScrollToComments = currentState[UrlConstants.ShouldScrollToCommentsParam];
        if (!!shouldScrollToComments) {
            if (equals(shouldScrollToComments, "true", true)) {
                const element = document.getElementsByClassName("wiki-comments-container");
                if (element && element.length > 0) {
                    element[0].scrollIntoView();
                }
            }
            delete currentState[UrlConstants.ShouldScrollToCommentsParam];
            historyService.replaceState(currentState);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        window.removeEventListener("beforeunload", this._onBeforeUnload);
        document.body.removeEventListener("fpsStarting", this._onFpsStarting);
    }

    public render(): JSX.Element {
        return (
            <CommentThread
                artifactInfo={buildCommentArtifactInfo(this.props.wikiArtifact)}
                commentsClient={this._wikiPageCommentsClient}
                readonly={this.props.isReadOnly}
                formatDisabled={true}
                supportsThreads={false}
                supportsReactions={this._supportsReactions}
                supportsImageUpload={this._supportsImageUpload}
                className="wiki-comments-container"
                onDirtyChange={this._onDirtyChange}
            />
        );
    }

    private _onDirtyChange = (isDirty: boolean): void => {
        this.setState({ isDirty });
    };

    private _onFpsStarting = (ev: CustomEvent): void => {
        if (this.state.isDirty) {
            ev.preventDefault();
            ev.detail.cancellationReason = Resources.UnsavedChangesDialogTitle;
            _showDiscardDialog(this.context.pageContext, () => {
                document.body.removeEventListener("fpsStarting", this._onFpsStarting);
                FastPageSwitch(ev.detail.pageContext, ev.detail.href, ev.detail.recordHistoryEntry, ev.detail.data);
            });
        }
    };

    private _onBeforeUnload = (ev: BeforeUnloadEvent): string | undefined => {
        if (this.state.isDirty) {
            ev.returnValue = Resources.UnsavedChangesDialogTitle;
            return ev.returnValue;
        }

        return undefined;
    };
}

function _showDiscardDialog(pageContext: IVssPageContext, onDiscard: () => void): void {
    const cancelNavigateAwayClassName = "cancel-discard-draft";
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => {
        return (
            <Dialog
                titleProps={{ text: Resources.UnsavedChangesDialogTitle }}
                onDismiss={onDismiss}
                lightDismiss={false}
                defaultActiveElement={`.${cancelNavigateAwayClassName}`}
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        onClick: onDismiss,
                        className: cancelNavigateAwayClassName
                    },
                    { text: Resources.Discard, danger: true, onClick: onDiscard }
                ]}
            >
                {Resources.ConfirmDiscardCommentDraft}
            </Dialog>
        );
    });
}

VssComponent.register("wiki-pagecomments", WikiPageComments);
